import ActionType from '../actionTypes/onlineDesignerActionTypes';
import { Fabric, Product } from '../../schemas/product';
import { Selected, SelectedSize } from '../../schemas/onlineDesigner';
import { IMember } from '../../components/TeamDialog/TeamDialog';

export interface OnlineDesignerState {
    loadProduct: CommonState & {
        data: Product | null;
    };
    selected: {
        data: Selected | null;
    };
    team: {
        data: IMember[] | null;
    };
}

interface CommonState {
    failed: boolean;
    loading: boolean;
    success: boolean;
    finished: boolean;
}

interface loadProductAction {
    type: string;
    data: Product;
}

interface selectFabricAction {
    type: string;
    fabric: Fabric;
}

interface selectSizesAction {
    type: string;
    sizes: SelectedSize[];
}

interface setMembersAction {
    type: string;
    data: IMember[];
}

const commonState = {
    failed: false,
    loading: false,
    success: false,
    finished: false,
};

const stateLoadProduct = 'loadProduct';
const stateSelected = 'selected';
const stateTeam = 'team';
type SubState =
    | typeof stateLoadProduct
    | typeof stateSelected
    | typeof stateTeam;

const getInitialState = (): OnlineDesignerState => ({
    [stateLoadProduct]: {
        ...commonState,
        data: null,
    },
    [stateSelected]: {
        ...commonState,
        data: {
            fabric: null,
            sizes: null,
        },
    },
    [stateTeam]: {
        ...commonState,
        data: null,
    },
});

const reset = (): OnlineDesignerState => getInitialState();

const start = (
    subStateName: SubState,
    state: OnlineDesignerState
): OnlineDesignerState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        failed: false,
        loading: true,
        success: false,
        finished: false,
    },
});

const successWithData = (
    subStateName: SubState,
    state: OnlineDesignerState,
    action: loadProductAction
): OnlineDesignerState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        data: action.data,
        failed: false,
        loading: false,
        success: true,
        finished: true,
    },
});

const fail = (
    subStateName: SubState,
    state: OnlineDesignerState
): OnlineDesignerState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        failed: true,
        loading: false,
        success: false,
        finished: true,
    },
});

const setSelectedFabric = (
    subStateName: SubState,
    state: OnlineDesignerState,
    action: selectFabricAction
): OnlineDesignerState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        data: {
            ...state[subStateName].data,
            fabric: action.fabric,
        },
    },
});

const setSelectedSizes = (
    subStateName: SubState,
    state: OnlineDesignerState,
    action: selectSizesAction
): OnlineDesignerState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        data: {
            ...state[subStateName].data,
            sizes: action.sizes,
        },
    },
});

const setMembers = (
    subStateName: SubState,
    state: OnlineDesignerState,
    action: setMembersAction
): OnlineDesignerState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        data: action.data,
    },
});

const reducer = (
    state = getInitialState(),
    action: any
): OnlineDesignerState => {
    switch (action.type) {
        case ActionType.loadProduct.start:
            return start(stateLoadProduct, state);
        case ActionType.loadProduct.success:
            return successWithData(stateLoadProduct, state, action);
        case ActionType.loadProduct.fail:
            return fail(stateLoadProduct, state);
        case ActionType.loadProduct.reset:
            return reset();

        case ActionType.selected.setFabric:
            return setSelectedFabric(stateSelected, state, action);
        case ActionType.selected.setSizes:
            return setSelectedSizes(stateSelected, state, action);

        case ActionType.team.setMembers:
            return setMembers(stateTeam, state, action);

        default:
            return state;
    }
};

export default reducer;
